import api, { type ApiResponse } from './api'

export interface MerchantAgreementDocument {
  agreementVersion: string
  effectiveDate: string
  effectiveDateDisplay: string
  templateSha256: string
  renderedDocumentSha256: string
  markdownContent: string
  acceptanceStatement: string
  actionLabel: string
  documentUrl: string
}

export interface AgreementConsentRequest {
  accepted: true
  agreementVersion: string
  effectiveDate: string
  renderedDocumentSha256: string
  partyFingerprint?: string
}

export type AgreementSelection =
  | { agreementAcceptanceId: string; agreementConsent?: never }
  | { agreementAcceptanceId?: never; agreementConsent: AgreementConsentRequest }

export type AgreementPartyType = 'BUSINESS' | 'INDIVIDUAL'
export type AgreementSubmissionType = 'BUSINESS' | 'PERSONAL'

export interface MerchantAgreementSubmissionParty {
  partyType: AgreementPartyType
  merchantLegalName: string
  partyFingerprint: string
  reusableAcceptanceId: string | null
  acceptedAt: number | null
  reusableAuthorizedUserName: string | null
}

export interface MerchantAgreementStatus {
  currentVersion: string
  partyType: AgreementPartyType | null
  merchantLegalName: string | null
  accepted: boolean
  acceptanceId: string | null
  acceptedAt: number | null
  requiresAction: boolean
  approvalBlocked: boolean
  capabilityBlocked: boolean
  graceEndsAt: number | null
}

export function getCurrentMerchantAgreement(): Promise<ApiResponse<MerchantAgreementDocument>> {
  return api.get('/v1/config/legal/merchant-services-agreement')
}

export function getHistoricalMerchantAgreement(
  version: string,
  effectiveDate: string,
): Promise<ApiResponse<MerchantAgreementDocument>> {
  return api.get(`/v1/config/legal/merchant-services-agreement/${encodeURIComponent(version)}`, {
    params: { effectiveDate },
  })
}

export function getMerchantAgreementStatus(): Promise<ApiResponse<MerchantAgreementStatus>> {
  return api.get('/v1/merchant/agreements/merchant-services/status')
}

export function getMerchantAgreementSubmissionParty(
  kycType: AgreementSubmissionType,
  candidateLegalName: string,
): Promise<ApiResponse<MerchantAgreementSubmissionParty>> {
  return api.post('/v1/merchant/agreements/merchant-services/submission-party', {
    kycType,
    candidateLegalName,
  })
}

export function acceptMerchantAgreement(
  consent: AgreementConsentRequest,
): Promise<ApiResponse<MerchantAgreementStatus>> {
  return api.post('/v1/merchant/agreements/merchant-services/accept', consent)
}
